/** Medal tiers for map time targets. Ticks are 100 Hz simulation ticks. */

export type Medal = 'author' | 'gold' | 'silver' | 'bronze';

/** All four medal thresholds in ticks (100 Hz). */
export interface MedalTimes {
  author: number;
  gold: number;
  silver: number;
  bronze: number;
}

/**
 * Returns the best medal earned for a given run time, or null if no medal is
 * earned (ticks > bronze, or medals undefined).
 *
 * A tier is earned when ticks <= that tier's threshold; the best (fastest)
 * qualifying tier wins.
 */
export function medalFor(ticks: number, medals: MedalTimes | undefined): Medal | null {
  if (medals === undefined) {
    return null;
  }
  if (ticks <= medals.author) return 'author';
  if (ticks <= medals.gold) return 'gold';
  if (ticks <= medals.silver) return 'silver';
  if (ticks <= medals.bronze) return 'bronze';
  return null;
}

/**
 * Derive medal thresholds from a headless bot's run time (ticks), using the
 * ratios from spec 0011: the bot is a fast-but-imperfect baseline, so author
 * sits just under it and the lower medals fan out for skilled → casual play.
 * Keeps a map's medals honest + reproducible instead of hand-guessed.
 */
export function medalsFromBot(botTicks: number): MedalTimes {
  return {
    author: Math.floor(botTicks * 0.9),
    gold: Math.floor(botTicks * 1.15),
    silver: Math.floor(botTicks * 1.5),
    bronze: Math.floor(botTicks * 2.2),
  };
}
